import {
  ProductHowItWorksContent,
  ProductHowItWorksTab,
} from '../shared/components/product-subpage/product-how-it-works/product-how-it-works.interface';
import {
  ProductProgramBenefitCard,
  ProductProgramBenefitStat,
  ProductProgramBenefitsContent,
} from '../shared/components/product-subpage/product-program-benefits/product-program-benefits.interface';
import { PATH_TRANSLATIONS, SupportedLang } from '../shared/config/localized-paths';

export { ProductHowItWorksContent } from '../shared/components/product-subpage/product-how-it-works/product-how-it-works.interface';
export { ProductProgramBenefitsContent } from '../shared/components/product-subpage/product-program-benefits/product-program-benefits.interface';

export const DIGITAL_INTRO_BANNER_ASSETS = {
  mediaImageSrc: '/assets/images/myce-student-dashboard-video-thumbnail.svg',
  videoSrc: 'oezriwbdik',
};

interface DigitalHowItWorksTabAssets {
  id: string;
  labelKey: string;
  cards: { imageSrc: string; imageAltKey: string; titleKey: string; descriptionKey: string }[];
}

const DIGITAL_HOW_IT_WORKS_I18N_KEYS = {
  headline: 'digital.howItWorks.headline',
};

const DIGITAL_HOW_IT_WORKS_TABS_ASSETS: DigitalHowItWorksTabAssets[] = [
  {
    id: 'leaders',
    labelKey: 'digital.howItWorks.tabs.leaders.label',
    cards: [
      {
        imageSrc: '/assets/images/myce-digital-leaders-school-adoption.svg',
        imageAltKey: 'digital.howItWorks.tabs.leaders.cards.card_0.imageAlt',
        titleKey: 'digital.howItWorks.tabs.leaders.cards.card_0.title',
        descriptionKey: 'digital.howItWorks.tabs.leaders.cards.card_0.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-leaders-school-setup.svg',
        imageAltKey: 'digital.howItWorks.tabs.leaders.cards.card_1.imageAlt',
        titleKey: 'digital.howItWorks.tabs.leaders.cards.card_1.title',
        descriptionKey: 'digital.howItWorks.tabs.leaders.cards.card_1.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-leaders-engagement.svg',
        imageAltKey: 'digital.howItWorks.tabs.leaders.cards.card_2.imageAlt',
        titleKey: 'digital.howItWorks.tabs.leaders.cards.card_2.title',
        descriptionKey: 'digital.howItWorks.tabs.leaders.cards.card_2.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-leaders-outcomes-data.svg',
        imageAltKey: 'digital.howItWorks.tabs.leaders.cards.card_3.imageAlt',
        titleKey: 'digital.howItWorks.tabs.leaders.cards.card_3.title',
        descriptionKey: 'digital.howItWorks.tabs.leaders.cards.card_3.description',
      },
    ],
  },
  {
    id: 'teachers',
    labelKey: 'digital.howItWorks.tabs.teachers.label',
    cards: [
      {
        imageSrc: '/assets/images/myce-digital-teachers-economy-settings.svg',
        imageAltKey: 'digital.howItWorks.tabs.teachers.cards.card_0.imageAlt',
        titleKey: 'digital.howItWorks.tabs.teachers.cards.card_0.title',
        descriptionKey: 'digital.howItWorks.tabs.teachers.cards.card_0.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-teachers-earn-spend.svg',
        imageAltKey: 'digital.howItWorks.tabs.teachers.cards.card_1.imageAlt',
        titleKey: 'digital.howItWorks.tabs.teachers.cards.card_1.title',
        descriptionKey: 'digital.howItWorks.tabs.teachers.cards.card_1.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-teachers-savings-goals.svg',
        imageAltKey: 'digital.howItWorks.tabs.teachers.cards.card_2.imageAlt',
        titleKey: 'digital.howItWorks.tabs.teachers.cards.card_2.title',
        descriptionKey: 'digital.howItWorks.tabs.teachers.cards.card_2.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-teachers-track-progress.svg',
        imageAltKey: 'digital.howItWorks.tabs.teachers.cards.card_3.imageAlt',
        titleKey: 'digital.howItWorks.tabs.teachers.cards.card_3.title',
        descriptionKey: 'digital.howItWorks.tabs.teachers.cards.card_3.description',
      },
    ],
  },
  {
    id: 'students',
    labelKey: 'digital.howItWorks.tabs.students.label',
    cards: [
      {
        imageSrc: '/assets/images/myce-digital-students-earn-income.svg',
        imageAltKey: 'digital.howItWorks.tabs.students.cards.card_0.imageAlt',
        titleKey: 'digital.howItWorks.tabs.students.cards.card_0.title',
        descriptionKey: 'digital.howItWorks.tabs.students.cards.card_0.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-students-save-goals.svg',
        imageAltKey: 'digital.howItWorks.tabs.students.cards.card_1.imageAlt',
        titleKey: 'digital.howItWorks.tabs.students.cards.card_1.title',
        descriptionKey: 'digital.howItWorks.tabs.students.cards.card_1.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-students-spend-wisely.svg',
        imageAltKey: 'digital.howItWorks.tabs.students.cards.card_2.imageAlt',
        titleKey: 'digital.howItWorks.tabs.students.cards.card_2.title',
        descriptionKey: 'digital.howItWorks.tabs.students.cards.card_2.description',
      },
      {
        imageSrc: '/assets/images/myce-digital-students-celebrate-progress.svg',
        imageAltKey: 'digital.howItWorks.tabs.students.cards.card_3.imageAlt',
        titleKey: 'digital.howItWorks.tabs.students.cards.card_3.title',
        descriptionKey: 'digital.howItWorks.tabs.students.cards.card_3.description',
      },
    ],
  },
];

export function buildHowItWorksContent(t: (key: string) => string): ProductHowItWorksContent {
  const tabs: ProductHowItWorksTab[] = DIGITAL_HOW_IT_WORKS_TABS_ASSETS.map((tabAsset) => ({
    id: tabAsset.id,
    label: t(tabAsset.labelKey),
    cards: tabAsset.cards.map((card) => ({
      imageSrc: card.imageSrc,
      imageAlt: t(card.imageAltKey),
      title: t(card.titleKey),
      description: t(card.descriptionKey),
    })),
  }));

  return {
    headline: t(DIGITAL_HOW_IT_WORKS_I18N_KEYS.headline),
    tabs,
  };
}

interface ProgramBenefitStatAssets {
  valueKey: string;
  labelKey: string;
}

interface ProgramBenefitCardAssets {
  iconSrc: string;
  titleKey: string;
  descriptionKey: string;
}

const DIGITAL_PROGRAM_BENEFITS_I18N_KEYS = {
  headline: 'digital.programBenefits.headline',
  description: 'digital.programBenefits.description',
  descriptionLinkText: 'digital.programBenefits.descriptionLinkText',
};

const DIGITAL_PROGRAM_BENEFITS_STATS_ASSETS: ProgramBenefitStatAssets[] = [
  { valueKey: 'digital.programBenefits.stats.stat_0.value', labelKey: 'digital.programBenefits.stats.stat_0.label' },
  { valueKey: 'digital.programBenefits.stats.stat_1.value', labelKey: 'digital.programBenefits.stats.stat_1.label' },
  { valueKey: 'digital.programBenefits.stats.stat_2.value', labelKey: 'digital.programBenefits.stats.stat_2.label' },
];

const DIGITAL_PROGRAM_BENEFITS_CARDS_ASSETS: ProgramBenefitCardAssets[] = [
  {
    iconSrc: '/assets/images/myce-program-benefits-habits.svg',
    titleKey: 'digital.programBenefits.cards.card_0.title',
    descriptionKey: 'digital.programBenefits.cards.card_0.description',
  },
  {
    iconSrc: '/assets/images/myce-program-benefits-home.svg',
    titleKey: 'digital.programBenefits.cards.card_1.title',
    descriptionKey: 'digital.programBenefits.cards.card_1.description',
  },
  {
    iconSrc: '/assets/images/myce-program-benefits-home-alt.svg',
    titleKey: 'digital.programBenefits.cards.card_2.title',
    descriptionKey: 'digital.programBenefits.cards.card_2.description',
  },
  {
    iconSrc: '/assets/images/myce-program-benefits-home-alt-2.svg',
    titleKey: 'digital.programBenefits.cards.card_3.title',
    descriptionKey: 'digital.programBenefits.cards.card_3.description',
  },
];

export function buildProgramBenefitsContent(
  t: (key: string) => string,
  lang: SupportedLang,
): ProductProgramBenefitsContent {
  const overviewPath = PATH_TRANSLATIONS[lang]['overview'];

  const stats: ProductProgramBenefitStat[] = DIGITAL_PROGRAM_BENEFITS_STATS_ASSETS.map((stat) => ({
    value: t(stat.valueKey),
    label: t(stat.labelKey),
  }));

  const cards: ProductProgramBenefitCard[] = DIGITAL_PROGRAM_BENEFITS_CARDS_ASSETS.map((card) => ({
    iconSrc: card.iconSrc,
    title: t(card.titleKey),
    description: t(card.descriptionKey),
  }));

  return {
    headline: t(DIGITAL_PROGRAM_BENEFITS_I18N_KEYS.headline),
    description: t(DIGITAL_PROGRAM_BENEFITS_I18N_KEYS.description),
    descriptionLink: {
      text: t(DIGITAL_PROGRAM_BENEFITS_I18N_KEYS.descriptionLinkText),
      routerLink: `/${lang}/${overviewPath}`,
    },
    stats,
    cards,
  };
}
